<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import DeleteBotDialog from './delete/DeleteBotDialog.vue';
import Button from 'next/button/Button.vue';
import { picoSearch } from '@chatwoot/pico-search';
import WgptBotRow from './components/WgptBotRow.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import { BaseTable } from 'dashboard/components-next/table';

const router = useRouter();
const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const searchQuery = ref('');
const deleteBotDialogRef = ref(false);
const fileInput = ref(null);
const cloningBotId = ref(null);
const exportingBotId = ref(null);

const records = computed(() => getters['wgptBots/getBots'].value);
const uiFlags = computed(() => getters['wgptBots/getUIFlags'].value);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    'name',
    'display_name',
    'description',
  ]);
});

onMounted(() => {
  store.dispatch('wgptBots/get');
});

const editBot = bot => {
  router.push({
    name: 'wgpt_bots_edit',
    params: { botId: bot.id },
  });
};

const openDeletePopup = response => {
  deleteBotDialogRef.value?.open(response);
};

const handleClone = async bot => {
  cloningBotId.value = bot.id;
  try {
    await store.dispatch('wgptBots/clone', bot.id);
    useAlert(t('WGPT_BOTS.CLONE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('WGPT_BOTS.CLONE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    cloningBotId.value = null;
  }
};

const exportBot = async id => {
  exportingBotId.value = id;
  try {
    const file = await store.dispatch('wgptBots/export', id);
    const url = window.URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  } catch (error) {
    useAlert(t('WGPT_BOTS.EXPORT.API.ERROR_MESSAGE'));
  } finally {
    exportingBotId.value = null;
  }
};

const importBot = () => {
  fileInput.value?.click();
};

const handleFileChange = async event => {
  try {
    const file = event.target.files[0];
    if (!file) return;

    const bot = await store.dispatch('wgptBots/import', file);
    editBot(bot);
  } catch (error) {
    useAlert(t('WGPT_BOTS.IMPORT.API.ERROR_MESSAGE'));
  } finally {
    event.target.value = null;
  }
};

const tableHeaders = computed(() => {
  return [
    t('WGPT_BOTS.LIST.TABLE_HEADER.NAME'),
    t('WGPT_BOTS.LIST.TABLE_HEADER.CREATED_BY'),
    t('WGPT_BOTS.LIST.TABLE_HEADER.LAST_UPDATED_BY'),
    t('WGPT_BOTS.LIST.TABLE_HEADER.LAST_UPDATED_AT'),
  ];
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('WGPT_BOTS.LIST.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('WGPT_BOTS.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('WGPT_BOTS.HEADER')"
        :description="$t('WGPT_BOTS.DESCRIPTION')"
        :link-text="$t('WGPT_BOTS.LEARN_MORE')"
        :search-placeholder="$t('WGPT_BOTS.SEARCH_PLACEHOLDER')"
        feature-name="wgpt_bots"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('WGPT_BOTS.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <div class="flex items-center flex-shrink-0 gap-4">
            <Button
              icon="i-lucide-import"
              :label="$t('WGPT_BOTS.HEADER_BTN_TEXTS.IMPORT')"
              :disabled="uiFlags.isImporting"
              :is-loading="uiFlags.isImporting"
              size="sm"
              slate
              @click="importBot"
            />
            <router-link :to="{ name: 'wgpt_bots_new' }">
              <Button
                :label="$t('WGPT_BOTS.HEADER_BTN_TEXTS.CREATE')"
                size="sm"
              />
            </router-link>
          </div>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery ? $t('WGPT_BOTS.NO_RESULTS') : $t('WGPT_BOTS.LIST.404')
        "
      >
        <template #row="{ items }">
          <WgptBotRow
            v-for="bot in items"
            :key="bot.id"
            :bot="bot"
            :is-cloning="cloningBotId === bot.id"
            :is-exporting="exportingBotId === bot.id"
            @delete="openDeletePopup(bot)"
            @clone="handleClone(bot)"
            @export="exportBot(bot.id)"
          />
        </template>
      </BaseTable>

      <DeleteBotDialog ref="deleteBotDialogRef" />

      <input
        ref="fileInput"
        type="file"
        accept=".wgpt"
        class="hidden"
        @change="handleFileChange"
      />
    </template>
  </SettingsLayout>
</template>
